import { formatPln } from '../catalog/money';
import { formatDecimal } from '../ui/numbers';
import type { PaymentsReceived, Turnover } from './types';

export type RevenueSource = 'zaakceptowana' | 'wyslana' | 'robocza' | 'brak';

const MONTHS = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
const SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/** '2026-11' -> 'listopad 2026' (short: 'lis'). */
export function monthLabel(month: string, opts: { short?: boolean } = {}): string {
  const i = Number(month.slice(5, 7)) - 1;
  return opts.short ? SHORT[i]! : `${MONTHS[i]} ${month.slice(0, 4)}`;
}

/** '2026-12' + 1 -> '2027-01'. */
export function shiftMonth(month: string, by: number): string {
  const total = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + by;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** The month of `now` in Warsaw ('YYYY-MM'). */
export function currentMonth(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit' }).formatToParts(now);
  return `${parts.find((p) => p.type === 'year')!.value}-${parts.find((p) => p.type === 'month')!.value}`;
}

/** 6986 bp -> '69,9%'; null (no revenue) -> '—'. */
export function marginLabel(bp: number | null): string {
  if (bp === null) return '—';
  const pct = Math.round(Math.abs(bp) / 10) / 10;
  return `${bp < 0 ? '−' : ''}${formatDecimal(pct)}%`;
}

const SOURCES: Record<RevenueSource, string> = {
  zaakceptowana: 'zaakceptowana wersja',
  wyslana: 'prognoza: wysłana wersja',
  robocza: 'prognoza: wersja robocza',
  brak: 'brak kosztorysu',
};
export const sourceLabel = (s: RevenueSource): string => SOURCES[s];

/** "Obroty": net of the issued invoices plus what came without an invoice (owner 2026-10-22). */
export const turnoverTotal = (t: Turnover): number => t.netGrosze + (t.withoutInvoiceGrosze ?? 0);

const FORM_PARTS: Array<[keyof PaymentsReceived, string]> = [
  ['formFakturaGrosze', 'faktura'],
  ['formPrzelewGrosze', 'przelew bez faktury'],
  ['formUmowaGrosze', 'umowa o dzieło'],
  ['formGotowkaGrosze', 'gotówka'],
  ['formFakturaInnaGrosze', 'faktura innego podmiotu'],
  ['formBarterGrosze', 'barter'],
];
/** "faktura 1 200,00 zł · gotówka 50,00 zł": the payments by settlement form, the empty ones left out. */
export const paymentsByForm = (p: PaymentsReceived): string =>
  FORM_PARTS.flatMap(([key, label]) => ((p[key] ?? 0) !== 0 ? [`${label} ${formatPln(p[key]!)}`] : [])).join(' · ');
